// pages/public/ResultsPage.jsx
// Shown after assessment submission
// Individual users: two cards - free snapshot download and pay for full report
// Enterprise and shared link users: thank you message only

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAssessment } from '../../context/AssessmentContext';
import axios from 'axios';

const ResultsPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user, sessionToken } = useAssessment();

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payConfig, setPayConfig] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [retries, setRetries] = useState(0);
  // 'idle' | 'loading' | 'ready' | 'failed'
  // Without this the PayPal container rendered as an empty div while the SDK
  // loaded, and stayed empty forever if the SDK never arrived, with nothing on
  // screen to say why.
  const [sdkState, setSdkState] = useState('idle');

  // The PayPal button is rendered once, so its createOrder and onApprove
  // callbacks capture whatever these values were at that moment. The session
  // is restored asynchronously, so they were frequently still null, and
  // checkout posted userId: undefined and got a 401 back. Reading through a
  // ref means the callbacks always see the current values.
  const checkoutRef = useRef({ userId: null, sessionToken: null, submissionId });
  useEffect(() => {
    checkoutRef.current = {
      userId: user?.id || report?.user?._id || null,
      sessionToken,
      submissionId,
    };
  }, [user, sessionToken, report, submissionId]);

  useEffect(() => {
    fetchReport();
    fetchPayConfig();
  }, [submissionId]);

  useEffect(() => {
    if (payConfig?.paypalConnected && payConfig?.paypalClientId && report) {
      loadPayPalSDK(payConfig.paypalClientId);
    }
  }, [payConfig, report]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`/reports/free/${submissionId}`);
      if (response.data.success) {
        const reportData = response.data.data.report;
        if (!reportData.freeContent && retries < 10) {
          setTimeout(() => {
            setRetries(r => r + 1);
            fetchReport();
          }, 3000);
          return;
        }
        setReport(reportData);
      }
    } catch (err) {
      console.error('Report fetch failed:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPayConfig = async () => {
    try {
      const response = await axios.get('/payment/config');
      if (response.data.success) setPayConfig(response.data.data);
    } catch (err) {
      console.error('Pay config failed:', err);
    }
  };

  const loadPayPalSDK = (clientId) => {
    const existing = document.getElementById('paypal-sdk');
    if (existing) { renderPayPalButton(); return; }
    setSdkState('loading');
    const script = document.createElement('script');
    script.id = 'paypal-sdk';
    script.src = `https://www.paypal.com/sdk/js?client-id=${clientId}&currency=USD`;
    script.onload = () => renderPayPalButton();
    // Previously there was no onerror handler. If the SDK failed to load the
    // container stayed blank indefinitely and the person had no way to tell
    // whether it was still loading or broken.
    script.onerror = () => {
      setSdkState('failed');
      // Let a later attempt re-add the tag rather than short circuiting on the
      // existing but dead script element.
      script.remove();
    };
    document.body.appendChild(script);
  };

  const retryPayPalSDK = () => {
    setSdkState('idle');
    if (payConfig?.paypalClientId) loadPayPalSDK(payConfig.paypalClientId);
  };

  const renderPayPalButton = () => {
    if (!window.paypal) { setSdkState('failed'); return; }
    setSdkState('ready');
    const container = document.getElementById('paypal-button-container');
    if (!container || container.children.length > 0) return;

    window.paypal.Buttons({
      style: { layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay', height: 44 },
      createOrder: async () => {
        const response = await axios.post('/payment/create-order', checkoutRef.current);
        return response.data.data.orderId;
      },
      onApprove: async (data) => {
        setPaying(true);
        try {
          const response = await axios.post(
            `/payment/capture/${data.orderID}`,
            checkoutRef.current
          );
          if (response.data.success) {
            navigate(`/report/free/${submissionId}`);
          }
        } catch (err) {
          setPayError('Payment failed. Please try again or contact support.');
        } finally {
          setPaying(false);
        }
      },
      onError: () => setPayError('Payment failed. Please try again.'),
      onCancel: () => setPayError('Payment was cancelled.'),
    }).render('#paypal-button-container');
  };

  const handleDownloadFreeReport = () => {
    navigate(`/report/free/${submissionId}`);
  };

  // Determine user type - check report, then context, then localStorage
  const storedAccessType = localStorage.getItem('esb_access_type');
  const userAccessType = report?.user?.accessType || user?.accessType || storedAccessType || null;
  // Only show the payment cards when we positively know this is an individual.
  // Defaulting an unknown access type to 'individual' meant an enterprise or
  // shared link user whose record was missing accessType would be asked to pay
  // $149 for a report their company had already covered. Falling back to the
  // thank you message is the safe direction to be wrong in.
  const isIndividual = userAccessType === 'individual';

  if (loading && retries < 3) {
    return (
      <div style={s.loadingPage}>
        <div style={s.spinner} />
        <h2 style={s.loadingTitle}>Processing Your Assessment</h2>
        <p style={s.loadingText}>
          Analysing your responses across 8 leadership dimensions...
        </p>
      </div>
    );
  }

  return (
    <div style={s.page}>
      <div style={s.header}>
        <div style={s.logo}>
          <span style={s.logoExec}>EXECUTIVE</span>
          <span style={s.logoSpring}>Springboard</span>
        </div>
      </div>

      <div style={s.container} className="esb-container">

        {/* Success message */}
        <div style={s.successBanner}>
          <div style={s.successIcon}>🎉</div>
          <div>
            <h1 style={s.successTitle}>
              Assessment Complete, {report?.user?.firstName || user?.firstName}!
            </h1>
            <p style={s.successText}>
              Your responses have been recorded across all 8 leadership dimensions.
            </p>
          </div>
        </div>

        {/* INDIVIDUAL: Two cards */}
        {isIndividual && (
          <>
            <p style={s.chooseText}>Choose how you would like to receive your results:</p>

            <div style={s.cardsRow} className="esb-grid-2">

              {/* Card 1: Free Snapshot */}
              <div style={s.card}>
                <div style={s.cardIconWrap}>
                  <span style={s.cardIcon}>📊</span>
                </div>
                <h2 style={s.cardTitle}>Free Snapshot Report</h2>
                <p style={s.cardDesc}>
                  Get your topline leadership scores and a summary of your comfort zones across all 8 dimensions. Available instantly.
                </p>
                <ul style={s.cardFeatures}>
                  <li style={s.cardFeature}><span style={s.featureCheck}>✓</span> Comfort zone scorecard</li>
                  <li style={s.cardFeature}><span style={s.featureCheck}>✓</span> Top 3 strengths</li>
                  <li style={s.cardFeature}><span style={s.featureCheck}>✓</span> Top 3 development areas</li>
                  <li style={s.cardFeature}><span style={s.featureCheck}>✓</span> Profile overview</li>
                </ul>
                <div style={s.cardPrice}>
                  <span style={s.priceTag}>Free</span>
                </div>
                <button style={s.btnFree} onClick={handleDownloadFreeReport}>
                  View My Free Snapshot →
                </button>
              </div>

              {/* Card 2: Full Paid Report */}
              <div style={{ ...s.card, ...s.cardPremium }}>
                <div style={s.popularBadge}>Most Popular</div>
                <div style={s.cardIconWrap}>
                  <span style={s.cardIcon}>🏆</span>
                </div>
                <h2 style={{ ...s.cardTitle, color: '#fff' }}>Comprehensive Report</h2>
                <p style={{ ...s.cardDesc, color: 'rgba(255,255,255,0.75)' }}>
                  Get the complete analysis personally reviewed by Steve Moss, plus a 30 minute one-on-one analytic consultation with him.
                </p>
                <ul style={s.cardFeatures}>
                  <li style={{ ...s.cardFeature, color: 'rgba(255,255,255,0.85)' }}><span style={s.featureCheckPremium}>✓</span> Everything in Free Snapshot</li>
                  <li style={{ ...s.cardFeature, color: 'rgba(255,255,255,0.85)' }}><span style={s.featureCheckPremium}>✓</span> Full dimension by dimension analysis</li>
                  <li style={{ ...s.cardFeature, color: 'rgba(255,255,255,0.85)' }}><span style={s.featureCheckPremium}>✓</span> Exception report with severity ratings</li>
                  <li style={{ ...s.cardFeature, color: 'rgba(255,255,255,0.85)' }}><span style={s.featureCheckPremium}>✓</span> 90 day development roadmap</li>
                  <li style={{ ...s.cardFeature, color: 'rgba(255,255,255,0.85)' }}><span style={s.featureCheckPremium}>✓</span> 30 min consultation with Steve Moss</li>
                  <li style={{ ...s.cardFeature, color: 'rgba(255,255,255,0.85)' }}><span style={s.featureCheckPremium}>✓</span> Delivered within 4 to 8 hours</li>
                </ul>
                <div style={s.cardPrice}>
                  <span style={{ ...s.priceTag, ...s.priceTagPremium }}>${payConfig?.assessmentPrice || 149}</span>
                  <span style={s.priceNote}>one time payment</span>
                </div>

                {payError && (
                  <div style={s.payError}>{payError}</div>
                )}

                {paying && (
                  <div style={s.payingRow}>
                    <div style={s.spinnerSmall} />
                    <span style={{ color: '#fff', fontSize: '14px' }}>Processing payment...</span>
                  </div>
                )}

                {payConfig?.paypalConnected ? (
                  <div>
                    <p style={s.secureNote}>🔒 Secure payment via PayPal</p>
                    {sdkState === 'failed' ? (
                      <div style={s.payFallbackBox}>
                        <p style={s.payFallbackText}>
                          The payment options could not be loaded. This is usually a network
                          or ad blocker issue.
                        </p>
                        <button type="button" style={s.payRetryBtn} onClick={retryPayPalSDK}>
                          Try again
                        </button>
                      </div>
                    ) : (
                      <>
                        {sdkState !== 'ready' && (
                          <div style={s.payLoadingBox}>
                            <div style={s.spinnerSmallDark} />
                            <span style={s.payLoadingText}>Loading secure payment options...</span>
                          </div>
                        )}
                        <div id="paypal-button-container" />
                      </>
                    )}
                  </div>
                ) : (
                  <div style={s.notConfiguredBox}>
                    <p style={s.notConfiguredText}>
                      Payment system is being configured. Please check back shortly or contact Executive Springboard directly.
                    </p>
                  </div>
                )}
              </div>

            </div>
          </>
        )}

        {/* ENTERPRISE / SHARED LINK: Thank you message */}
        {!isIndividual && (
          <div style={s.thankYouBox}>
            <div style={s.thankYouIcon}>📬</div>
            <h2 style={s.thankYouTitle}>Thank You for Your Submission</h2>
            <p style={s.thankYouText}>
              You will receive your comprehensive leadership report by email after a thorough analysis by the Executive Springboard team.
              Please allow <strong>4 to 8 hours</strong> from the time of submission.
            </p>
            <p style={s.thankYouEmail}>
              Report will be sent to: <strong>{report?.user?.email || user?.email}</strong>
            </p>
          </div>
        )}

      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', backgroundColor: 'var(--color-off-white)', fontFamily: 'var(--font-primary)' },
  loadingPage: { minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '16px', textAlign: 'center', padding: '24px' },
  spinner: { width: '48px', height: '48px', border: '5px solid var(--color-light-gray)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  spinnerSmallDark: { width: '18px', height: '18px', border: '3px solid rgba(10,35,66,0.15)', borderTopColor: 'var(--color-teal)', borderRadius: '50%', animation: 'spin 0.8s linear infinite', flexShrink: 0 },
  payLoadingBox: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', padding: '18px', minHeight: '52px' },
  payLoadingText: { fontSize: '13px', color: 'rgba(255,255,255,0.85)' },
  payFallbackBox: { padding: '16px', borderRadius: '10px', backgroundColor: 'rgba(255,255,255,0.1)', textAlign: 'center' },
  payFallbackText: { fontSize: '13px', color: 'rgba(255,255,255,0.9)', marginBottom: '12px', lineHeight: '1.6' },
  payRetryBtn: { backgroundColor: '#fff', color: 'var(--color-primary)', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  spinnerSmall: { width: '20px', height: '20px', border: '3px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  loadingTitle: { fontSize: '24px', fontWeight: '700', color: 'var(--color-primary)', margin: 0 },
  loadingText: { fontSize: '15px', color: 'var(--color-text-light)', maxWidth: '400px' },
  header: { backgroundColor: 'var(--color-primary)', padding: '0 24px', height: '64px', display: 'flex', alignItems: 'center' },
  logo: { display: 'flex', flexDirection: 'column', gap: '1px' },
  logoExec: { fontSize: '9px', fontWeight: '700', letterSpacing: '3px', color: 'var(--color-teal)', lineHeight: 1 },
  logoSpring: { fontSize: '18px', fontWeight: '700', color: '#FFFFFF', lineHeight: 1 },
  container: { maxWidth: '860px', margin: '0 auto', padding: '40px 24px 80px' },
  successBanner: { display: 'flex', gap: '16px', alignItems: 'center', backgroundColor: '#E8F8EF', border: '1px solid #A9DFBF', borderRadius: '12px', padding: '24px', marginBottom: '32px' },
  successIcon: { fontSize: '36px', flexShrink: 0 },
  successTitle: { fontSize: '22px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '4px' },
  successText: { fontSize: '15px', color: 'var(--color-text-light)' },
  chooseText: { fontSize: '16px', color: 'var(--color-text-light)', textAlign: 'center', marginBottom: '24px' },
  cardsRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  card: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', padding: '32px', boxShadow: 'var(--shadow-md)', display: 'flex', flexDirection: 'column', position: 'relative' },
  cardPremium: { backgroundColor: 'var(--color-primary)', border: 'none' },
  popularBadge: { position: 'absolute', top: '-12px', left: '50%', transform: 'translateX(-50%)', backgroundColor: 'var(--color-teal)', color: '#fff', fontSize: '12px', fontWeight: '700', padding: '4px 16px', borderRadius: '100px', whiteSpace: 'nowrap' },
  cardIconWrap: { marginBottom: '16px' },
  cardIcon: { fontSize: '40px' },
  cardTitle: { fontSize: '20px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '10px' },
  cardDesc: { fontSize: '14px', color: 'var(--color-text-light)', lineHeight: '1.6', marginBottom: '20px' },
  cardFeatures: { listStyle: 'none', padding: 0, margin: '0 0 24px', display: 'flex', flexDirection: 'column', gap: '8px', flex: 1 },
  cardFeature: { display: 'flex', gap: '8px', alignItems: 'flex-start', fontSize: '14px', color: 'var(--color-text)' },
  featureCheck: { color: 'var(--color-teal)', fontWeight: '700', flexShrink: 0 },
  featureCheckPremium: { color: 'var(--color-teal)', fontWeight: '700', flexShrink: 0 },
  cardPrice: { display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '20px' },
  priceTag: { fontSize: '32px', fontWeight: '800', color: 'var(--color-primary)' },
  priceTagPremium: { color: '#fff' },
  priceNote: { fontSize: '13px', color: 'rgba(255,255,255,0.5)' },
  btnFree: { width: '100%', backgroundColor: 'var(--color-off-white)', color: 'var(--color-primary)', padding: '14px', borderRadius: '8px', border: '2px solid var(--color-border)', fontSize: '15px', fontWeight: '600', cursor: 'pointer' },
  payError: { backgroundColor: 'rgba(255,255,255,0.1)', color: '#FFB3B3', borderRadius: '8px', padding: '10px 14px', fontSize: '13px', marginBottom: '12px' },
  payingRow: { display: 'flex', alignItems: 'center', gap: '10px', justifyContent: 'center', padding: '12px', marginBottom: '8px' },
  secureNote: { fontSize: '12px', color: 'rgba(255,255,255,0.5)', textAlign: 'center', marginBottom: '10px' },
  notConfiguredBox: { backgroundColor: 'rgba(255,255,255,0.05)', borderRadius: '8px', padding: '16px' },
  notConfiguredText: { fontSize: '13px', color: 'rgba(255,255,255,0.6)', textAlign: 'center', lineHeight: '1.6' },
  thankYouBox: { backgroundColor: '#fff', borderRadius: '16px', border: '1px solid var(--color-border)', padding: '60px 40px', textAlign: 'center', boxShadow: 'var(--shadow-md)' },
  thankYouIcon: { fontSize: '56px', marginBottom: '20px' },
  thankYouTitle: { fontSize: '26px', fontWeight: '700', color: 'var(--color-primary)', marginBottom: '16px' },
  thankYouText: { fontSize: '16px', color: 'var(--color-text-light)', lineHeight: '1.8', maxWidth: '500px', margin: '0 auto 16px' },
  thankYouEmail: { fontSize: '14px', color: 'var(--color-text-light)' },
};

export default ResultsPage;
